import React, { useEffect, useRef, useState } from 'react';
import { Card, CardTheme, CardHeaderTheme, CardBorderStyle, CardBorderRadius, Button, ButtonTheme } from '@apply/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { NameAndLicensePanel } from '../../../components/NameAndLicensePanel/NameAndLicensePanel';
import { BankWirePanel } from '../../../components/BankWirePanel/BankWirePanel';
import { ContactAddressSection } from '../../../components/ContactAddressSection/ContactAddressSection';
import { HomeAddressSection } from '../../../components/HomeAddressSection/HomeAddressSection';
import { useBrokerSettingsSubmit } from './hooks/useBrokerSettingsSubmit';
import { useBrokerSettingsValidation } from './hooks/useBrokerSettingsValidation';
import { useBrokerDetailsStore } from '../../../state/brokerDetailsStore';
import { BankWireLast4Digits } from './components/BankWireLast4Digits';
import { CompanyInfoPanel } from '../../../components/CompanyInfoPanel/CompanyInfoPanel';
import { DateOfBirthField } from '../../../components/DateOfBirthField';
import { ValidationGroup } from 'devextreme-react';
import './scss/_brokerSettingsDetailsPanel.scss';

export const BrokerDetailsPanel: React.FC = () => {
    const vgRef = useRef<ValidationGroup>(null);
    const { validate } = useBrokerSettingsValidation();
    const submit = useBrokerSettingsSubmit(() => validate({ showCompanyPanel, vgRef }));
    const storeId = useBrokerDetailsStore((s) => s.id);
    const [showCompanyPanel, set_showCompanyPanel] = useState(false);

    useEffect(() => {
        const s = useBrokerDetailsStore.getState();
        set_showCompanyPanel(!!s.businessName);
    }, [storeId]);

    const cardHeader = () => {
        return (
            <>
                <div className='brokerSettingsCardHeader'>
                    <FontAwesomeIcon
                        icon={faUser}
                        className='icon'
                        style={{ paddingRight: '10px' }}
                    />
                    <h4>User Details</h4>
                </div>
            </>
        );
    };

    return (
        <>
            <div className='brokerSettingsDetailsPanel'>
                <Card
                    theme={CardTheme.White}
                    headerTheme={CardHeaderTheme.Gray}
                    borderStyle={CardBorderStyle.SolidDarkerBorder}
                    borderRadius={CardBorderRadius.Small}
                    header={cardHeader()}
                    collapsible={true}
                    width='100%'
                >
                    <ValidationGroup
                        id='vgBrokerDetailsPanel'
                        ref={vgRef}
                    >
                        <div className='brokerSettingsDetailsPanel-disclaimer'>
                            If fields are locked, please contact BFI to make changes.
                        </div>

                        <div className='brokerSettingsDetailsPanel-content'>
                            <h2>Broker Information</h2>
                            <NameAndLicensePanel />
                            <div className='brokerSettingsDetailsPanel-content-section'>
                                <CompanyInfoPanel
                                    showCompanyPanel={showCompanyPanel}
                                    readOnlyNameField={true}
                                    readOnlyStateField={true}
                                    readOnlyTitleField={true}
                                />
                            </div>
                            <BankWirePanel />
                            <BankWireLast4Digits />
                            <div className='brokerSettingsDetailsPanel-content-section'>
                                <ContactAddressSection
                                    readOnlyPrimaryEmail={true}
                                    showPrimaryEmailWarning={false}
                                    maxPhoneFieldColumns={2}
                                />
                            </div>
                            <div className='brokerSettingsDetailsPanel-content-section'>
                                <HomeAddressSection />
                            </div>
                            <div className='brokerSettingsDetailsPanel-content-section'>
                                <DateOfBirthField />
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                id='updateBrokerSettingsButton'
                                theme={ButtonTheme.Primary}
                                disabled={submit.isSubmitting}
                                width='200px'
                                height='42px'
                                handleClick={() => submit.handleBrokerSettingsSubmit()}
                            >
                                {submit.isSubmitting ? 'Updating...' : 'Update Information'}
                            </Button>
                        </div>
                    </ValidationGroup>
                </Card>
            </div>
        </>
    );
};
